import { TabNavigationItemName, useSettings, useTutorialsCompleted } from '@capsicum/common';
import { usePoblanoTutorialEligibility } from '@capsicum/features/feeds/shared';
import { useAtom } from 'jotai';
import { useCallback, useMemo } from 'react';

import { walletTutorialCompletedAtom } from '../data-access';

type UseWalletTutorialProps = {
	enabled: boolean;
};

const TUTORIAL_NAME = 'wallet-onboarding-tutorial';

export const useWalletTutorial = ({ enabled }: UseWalletTutorialProps = { enabled: true }) => {
	const { isAlreadyCompleted } = useTutorialsCompleted();
	const [walletTutorialCompleted, setWalletTutorialCompletedAtom] = useAtom(walletTutorialCompletedAtom);

	const {
		areSettingsLoading,
		settings: { areTutorialsEnabled, isBrowserSectionEnabled: isBrowserSectionEnabledSetting, tabNavigationItems }
	} = useSettings();

	const { isBrowserSectionEnabled, isChartsSectionEnabled, isMarketplaceSectionEnabled } = useMemo(() => {
		return {
			isBrowserSectionEnabled:
				isBrowserSectionEnabledSetting &&
				tabNavigationItems.some(({ name }) => name === TabNavigationItemName.Browser),

			isChartsSectionEnabled: tabNavigationItems.some(({ name }) => name === TabNavigationItemName.Charts),

			isMarketplaceSectionEnabled: tabNavigationItems.some(({ name }) => name === TabNavigationItemName.Market)
		};
	}, [tabNavigationItems, isBrowserSectionEnabledSetting]);

	const {
		isEnabled,
		isExistingUser,
		isLoading: isPoblanoTutorialLoading
	} = usePoblanoTutorialEligibility({ enabled: enabled && areTutorialsEnabled.walletOnboarding });

	const onDone = useCallback(() => {
		setWalletTutorialCompletedAtom(true);
	}, [setWalletTutorialCompletedAtom]);

	const name = `${TUTORIAL_NAME}-${isExistingUser ? 'existing' : 'new'}`;

	return {
		isBrowserSectionEnabled,
		isChartsSectionEnabled,
		isEnabled: isEnabled && (isExistingUser ? !walletTutorialCompleted : !isAlreadyCompleted(name)),
		isExistingUser,
		isLoading: areSettingsLoading || isPoblanoTutorialLoading,
		isMarketplaceSectionEnabled,
		name,
		onDone
	};
};
